import React from "react";
import { Calendar, Clock, MapPin, Navigation } from "lucide-react";
import { Reveal, Chapter, MaskLines } from "./Reveal";
import { Divider, WorldMap, Stamp } from "./Deco";
import { WEDDING } from "../content";

export const Invitation = ({ t }) => {
  return (
    <section id="invitation" className="relative paper py-24 sm:py-36 px-5">
      <div className="absolute inset-0 pointer-events-none opacity-[0.12]">
        <WorldMap className="w-full h-full" color="#8a6d2f" />
      </div>

      <div className="relative max-w-3xl mx-auto text-center">
        <Chapter>{t.chapter}</Chapter>
        <MaskLines
          lines={[t.title]}
          className="font-serif text-4xl sm:text-6xl leading-[1.05] text-[var(--forest)]"
        />
        <Reveal delay={0.15}>
          <p className="font-script text-4xl sm:text-5xl text-[var(--gold-dark)] mt-2">
            {t.script} <span className="align-middle text-2xl">♡</span>
          </p>
        </Reveal>

        <Reveal delay={0.2}>
          <Divider className="my-8 text-[var(--gold-dark)]" />
        </Reveal>

        <Reveal delay={0.25}>
          <p className="font-serif text-lg sm:text-2xl leading-relaxed text-[var(--olive)]/85 max-w-xl mx-auto">
            {t.body}
          </p>
        </Reveal>

        {/* date / time / venue */}
        <Reveal delay={0.15}>
          <div className="mt-14 grid sm:grid-cols-2 gap-6 text-left">
            <InfoRow icon={<Calendar size={22} />} label={t.when} value={t.whenDate} />
            <InfoRow icon={<Clock size={22} />} label={t.time} value={t.time} showLabelValue />
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-6 flex items-start gap-4 text-left border-t border-[var(--gold-dark)]/25 pt-6">
            <span className="text-[var(--gold-dark)] mt-1"><MapPin size={22} /></span>
            <div>
              <p className="font-serif font-semibold text-2xl text-[var(--forest)]">{t.venue}</p>
              <p className="font-sans text-sm text-[var(--olive)]/70 mt-1 leading-relaxed max-w-md">{t.address}</p>
              <a
                data-testid="maps-link"
                href={WEDDING.mapsUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-full bg-[var(--forest)] text-[var(--cream)] text-xs tracking-[0.18em] uppercase font-sans transition-all duration-300 hover:bg-[var(--olive)] hover:gap-3"
              >
                <Navigation size={14} /> {t.map}
              </a>
            </div>
          </div>
        </Reveal>

        {/* families */}
        <Reveal delay={0.15}>
          <div className="mt-14 border border-[var(--gold-dark)]/40 rounded-lg p-6 sm:p-8">
            <p className="font-serif tracking-[0.3em] text-sm text-[var(--gold-dark)] mb-6">{t.families}</p>
            <div className="grid grid-cols-2 divide-x divide-[var(--gold-dark)]/30">
              <div className="px-3">
                <p className="font-sans text-[11px] tracking-[0.2em] text-[var(--olive)]/60">{t.groomSide}</p>
                <p className="font-serif text-xl sm:text-2xl text-[var(--forest)] mt-1">{t.groomParents}</p>
              </div>
              <div className="px-3">
                <p className="font-sans text-[11px] tracking-[0.2em] text-[var(--olive)]/60">{t.brideSide}</p>
                <p className="font-serif text-xl sm:text-2xl text-[var(--forest)] mt-1">{t.brideParents}</p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-col items-center">
            <div className="text-[var(--gold-dark)]"><Stamp /></div>
            <p className="font-script text-4xl text-[var(--forest)] mt-6 leading-tight">{WEDDING.bride}</p>
            <span className="font-serif text-xl text-[var(--gold-dark)]">&amp;</span>
            <p className="font-script text-4xl text-[var(--forest)] leading-tight">{WEDDING.groom}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const InfoRow = ({ icon, label, value, showLabelValue }) => (
  <div className="flex items-center gap-4">
    <span className="text-[var(--gold-dark)]">{icon}</span>
    <div>
      {showLabelValue ? (
        <p className="font-serif text-2xl text-[var(--forest)]">{value}</p>
      ) : (
        <>
          <p className="font-sans text-[11px] tracking-[0.2em] text-[var(--olive)]/60 uppercase">{label}</p>
          <p className="font-serif text-xl text-[var(--forest)]">{value}</p>
        </>
      )}
    </div>
  </div>
);
